// SAPIENTIA — Multilingual dictionary (PT / EN / ES)
// Kept flat + explicit for clarity.

export const translations = {
  pt: {
    lang_label: "Português",
    nav: {
      about: "Manifesto",
      architecture: "Arquitetura",
      services: "Serviços",
      process: "Como Trabalhamos",
      technology: "Tecnologia",
      diagnostic: "Diagnóstico",
      cta: "Agendar Diagnóstico",
    },
    hero: {
      overline: "Arquitetura da Gestão · Sapientia",
      title: "Projetamos a Arquitetura da Gestão que sustenta empresas em crescimento.",
      subtitle:
        "Integramos estratégia, processos, controladoria, tecnologia e inteligência em um único ecossistema — para que sua empresa cresça com controle, previsibilidade e decisões orientadas por dados.",
      primary_cta: "Agendar Diagnóstico",
      secondary_cta: "Falar no WhatsApp",
      metric_1: { value: "6", label: "Pilares integrados" },
      metric_2: { value: "10+", label: "Anos de experiência" },
      metric_3: { value: "100%", label: "Foco em resultado" },
    },
    problems: {
      overline: "Diagnóstico de Realidade",
      title: "As dores que travam o crescimento da sua empresa",
      subtitle:
        "Reconhece algum destes cenários? Você não está sozinho — a maioria dos negócios em transição enfrenta os mesmos padrões.",
      items: [
        "Faturamento cresce, mas o lucro não acompanha.",
        "Fluxo de caixa desorganizado e imprevisível.",
        "Falta de indicadores para decidir com segurança.",
        "Processos informais, dependentes de pessoas.",
        "Dependência excessiva do proprietário no dia a dia.",
        "Planilhas em excesso, controles frágeis.",
        "Pouco controle sobre custos, margem e resultado.",
        "Muito retrabalho, baixa produtividade.",
      ],
    },
    manifesto: {
      overline: "Manifesto Sapientia",
      title: "Gestão não é um conjunto de ferramentas. Gestão é um sistema.",
      lines: [
        "Toda organização nasce de uma ideia. Poucas, porém, são construídas para crescer de forma sustentável.",
        "Com o tempo, processos são criados sem padrão, controles deixam de acompanhar a operação e as decisões passam a depender mais da experiência das pessoas do que de informações confiáveis.",
        "O problema raramente está na falta de esforço — está na ausência de uma arquitetura que conecte estratégia, pessoas, processos, informações e tecnologia.",
        "Projetamos, estruturamos e implementamos sistemas de gestão que transformam organizações em ambientes previsíveis, eficientes e orientados por dados.",
      ],
      quote: "Crescer é importante. Crescer com controle é essencial.",
      quote_author: "— Sapientia Consultores Associados",
    },
    solution: {
      overline: "Nossa Abordagem",
      title: "Não entregamos apenas recomendações. Projetamos a arquitetura que sustenta o crescimento.",
      body_1:
        "Acreditamos que empresas fortes não dependem exclusivamente de líderes extraordinários. Dependem de processos consistentes, informações confiáveis, governança sólida e tecnologia aplicada de forma estratégica.",
      body_2:
        "Por isso nossa atuação vai da compreensão profunda do negócio até o monitoramento contínuo dos indicadores — sempre integrando estratégia, operação e tecnologia em um único modelo.",
      bullets: [
        "Estratégia traduzida em objetivos, KPIs e iniciativas (BSC).",
        "Processos, controladoria e tecnologia como um só ecossistema.",
        "Dashboards que alimentam o Mapa Estratégico em tempo real.",
      ],
    },
    architecture: {
      overline: "Nossa Metodologia",
      title: "Arquitetura da Gestão",
      lead:
        "Uma metodologia que conecta planejamento estratégico, Balanced Scorecard, processos, controladoria, sistemas de informação, Business Intelligence, dashboards e Inteligência Artificial em uma única estrutura.",
      caption: "Estratégia · Processos · Tecnologia · Inteligência · Resultados",
      pillars_overline: "Seis Pilares Integrados",
      pillars_title: "A estrutura invisível que faz uma empresa crescer com solidez",
      pillars: [
        {
          title: "Arquitetura Estratégica",
          desc: "Missão, visão, valores, Mapa Estratégico (BSC), objetivos, indicadores e governança.",
        },
        {
          title: "Arquitetura de Processos",
          desc: "Mapeamento, padronização, redesenho e gestão por processos alinhados à estratégia.",
        },
        {
          title: "Arquitetura da Informação",
          desc: "Estruturação dos dados, integração de sistemas e qualidade dos indicadores.",
        },
        {
          title: "Arquitetura Tecnológica",
          desc: "Automação, desenvolvimento de sistemas, IA, integrações e transformação digital.",
        },
        {
          title: "Arquitetura de Controle",
          desc: "Controladoria, custos, orçamento, riscos, compliance e desempenho organizacional.",
        },
        {
          title: "Arquitetura da Inteligência",
          desc: "Business Intelligence, dashboards executivos, analytics e monitoramento contínuo.",
        },
      ],
      cycle_title: "Um ciclo permanente de gestão",
      cycle: [
        "Planejamento Estratégico",
        "Balanced Scorecard",
        "Processos Organizacionais",
        "Sistemas de Informação",
        "Controladoria",
        "Business Intelligence",
        "Dashboards Gerenciais",
        "Monitoramento de Indicadores",
        "Tomada de Decisão",
        "Melhoria Contínua",
      ],
    },
    architecture_cycle: {
      center: {
        overline: "Ciclo",
        title_line_1: "Arquitetura",
        title_line_2: "da Gestão",
      },
      phases: [
        {
          name: "Planejar",
          items: ["Planejamento Estratégico", "Balanced Scorecard", "Objetivos e KPIs"],
        },
        {
          name: "Estruturar",
          items: ["Processos Organizacionais", "Sistemas de Informação", "Controladoria"],
        },
        {
          name: "Monitorar",
          items: ["Business Intelligence", "Dashboards Gerenciais", "Monitoramento de KPIs"],
        },
        {
          name: "Evoluir",
          items: ["Tomada de Decisão", "Melhoria Contínua"],
        },
      ],
    },
    metodo: {
      overline: "Método Sapientia®",
      title: "Da estratégia à execução. Da informação à inteligência.",
      subtitle:
        "Sete etapas conectadas por uma governança e controladoria centralizadas — sustentadas pelos pilares transversais de pessoas, cultura, dados, tecnologia e ética. É o método que transforma estratégia em resultado real, e resultado real em vantagem competitiva.",
      quote:
        "Esse é o ciclo que transforma estratégia em resultados e mantém sua organização sempre um passo à frente.",
      chips: ["Planejar", "Medir", "Executar", "Analisar", "Evoluir"],
    },
    services: {
      overline: "O que fazemos",
      title: "Serviços integrados de gestão e tecnologia",
      subtitle:
        "Um portfólio conectado que resolve o problema de ponta a ponta — da estratégia à execução automatizada.",
      items: [
        {
          title: "Controladoria Estratégica",
          desc: "Estruturação financeira, orçamento, DRE gerencial e indicadores para decisão baseada em dados.",
        },
        {
          title: "Planejamento Estratégico",
          desc: "Visão, metas e desdobramento em plano de ação com indicadores claros e cadência de execução.",
        },
        {
          title: "Gestão de Processos",
          desc: "Mapeamento, padronização e otimização dos processos-chave do negócio para escalar com organização.",
        },
        {
          title: "Business Intelligence",
          desc: "Dashboards e painéis executivos que transformam dado em decisão em tempo real.",
        },
        {
          title: "Automação de Processos",
          desc: "Automatização de rotinas administrativas, financeiras e operacionais com integração entre sistemas.",
        },
        {
          title: "Inteligência Artificial",
          desc: "IA aplicada à gestão: previsões, análise de padrões, agentes internos e assistentes inteligentes.",
        },
        {
          title: "Desenvolvimento de Sistemas",
          desc: "Soluções sob medida quando o software de mercado não resolve o problema real da operação.",
        },
        {
          title: "Consultoria Pública",
          desc: "Modernização, transparência e eficiência para prefeituras, autarquias e consórcios públicos.",
        },
      ],
    },
    differentials: {
      overline: "Por que a Sapientia",
      title: "Diferenciais que sustentam o alto padrão de entrega",
      items: [
        {
          title: "Mais de uma década de experiência",
          desc: "Trajetória sólida em projetos complexos nos setores privado e público.",
        },
        {
          title: "Equipe multidisciplinar",
          desc: "Gestão, controladoria, engenharia de dados e desenvolvimento em um só time.",
        },
        {
          title: "Integração entre gestão e tecnologia",
          desc: "Não separamos consultoria e TI — trabalhamos os dois lados como um só.",
        },
        {
          title: "Soluções personalizadas",
          desc: "Nada de receita pronta. Modelamos cada projeto à realidade da empresa.",
        },
        {
          title: "Foco em resultados",
          desc: "Indicadores objetivos acompanham o antes e depois de cada implantação.",
        },
        {
          title: "Implantação prática",
          desc: "Ficamos junto do time do cliente até o novo modelo estar rodando.",
        },
        {
          title: "Acompanhamento contínuo",
          desc: "Ciclo de melhoria contínua após o go-live para sustentar os ganhos.",
        },
      ],
    },
    process: {
      overline: "Como Trabalhamos",
      title: "Um método claro em cinco etapas",
      subtitle:
        "Do entendimento profundo do negócio até o monitoramento contínuo dos resultados.",
      steps: [
        {
          title: "Diagnóstico",
          desc: "Imersão completa: financeiro, processos, pessoas, sistemas e cultura.",
        },
        {
          title: "Planejamento",
          desc: "Plano de ação, priorização e desenho de indicadores de sucesso.",
        },
        {
          title: "Implantação",
          desc: "Execução conjunta, revisão de processos e estruturação da controladoria.",
        },
        {
          title: "Automação",
          desc: "Digitalização, integração de sistemas, dashboards e IA aplicada.",
        },
        {
          title: "Monitoramento",
          desc: "Acompanhamento contínuo dos indicadores e refinamento do modelo.",
        },
      ],
    },
    technology: {
      overline: "Tecnologia que sustenta a decisão",
      title: "Inteligência de negócios, dados e automação em um só ecossistema",
      subtitle:
        "Selecionamos e integramos as ferramentas certas para cada realidade — do escritório da PME ao ambiente do órgão público.",
      stack: [
        "Power BI",
        "SQL Server",
        "Python",
        "APIs & Integrações",
        "No-Code",
        "Inteligência Artificial",
        "Dashboards Executivos",
        "Automação de Processos",
      ],
    },
    cta_final: {
      overline: "Diagnóstico Empresarial",
      title: "Está na hora de administrar sua empresa com inteligência.",
      subtitle:
        "Solicite um Diagnóstico Empresarial Inteligente e descubra onde estão as maiores alavancas de resultado do seu negócio.",
      button: "Quero um Diagnóstico Empresarial",
    },
    form: {
      overline: "Diagnóstico Empresarial Inteligente",
      title: "Comece pelo entendimento — não pela venda.",
      subtitle:
        "Preencha as informações abaixo. Elas servirão como base para nossa primeira reunião e para uma proposta comercial verdadeiramente sob medida.",
      fields: {
        full_name: "Nome completo",
        email: "E-mail corporativo",
        whatsapp: "WhatsApp",
        company: "Empresa",
        role: "Cargo",
        sector: "Setor de atuação",
        size: "Porte da empresa",
        size_placeholder: "Selecione o porte",
        size_options: [
          { value: "1-10", label: "Até 10 colaboradores" },
          { value: "11-50", label: "11 a 50 colaboradores" },
          { value: "51-200", label: "51 a 200 colaboradores" },
          { value: "201-500", label: "201 a 500 colaboradores" },
          { value: "500+", label: "Mais de 500 colaboradores" },
          { value: "public", label: "Órgão público / Autarquia" },
        ],
        challenges: "Principais desafios atuais",
        challenges_ph:
          "Ex.: falta de indicadores, fluxo de caixa desorganizado, dependência do sócio...",
        goals: "Objetivos para os próximos 12 meses",
        goals_ph:
          "Ex.: profissionalizar a gestão, escalar operação, implantar controladoria, integrar sistemas...",
        consent: "Autorizo o contato pela Sapientia e concordo com a Política de Privacidade.",
        submit: "Solicitar Diagnóstico",
        sending: "Enviando…",
      },
      success_title: "Recebemos suas informações.",
      success_body:
        "Em breve um de nossos consultores entrará em contato para agendar o Diagnóstico Empresarial Inteligente.",
      error: "Não foi possível enviar. Tente novamente em instantes.",
      validation: {
        required: "Campo obrigatório",
        email: "E-mail inválido",
        consent: "É necessário autorizar o contato",
      },
    },
    footer: {
      tagline:
        "Combinando gestão, dados e tecnologia para estruturar empresas que crescem com controle.",
      nav_title: "Navegação",
      contact_title: "Contato",
      legal_title: "Legal",
      privacy: "Política de Privacidade",
      terms: "Termos de Uso",
      copyright: "© {year} Sapientia Consultores Associados EIRELI. Todos os direitos reservados.",
      address: "Curitiba — Paraná, Brasil",
    },
    whatsapp_msg: "Olá! Vim pelo site da Sapientia e gostaria de conversar sobre gestão.",
  },

  en: {
    lang_label: "English",
    nav: {
      about: "Manifesto",
      architecture: "Architecture",
      services: "Services",
      process: "How We Work",
      technology: "Technology",
      diagnostic: "Diagnostic",
      cta: "Book a Diagnostic",
    },
    hero: {
      overline: "Management Architecture · Sapientia",
      title: "We design the Management Architecture that sustains growing companies.",
      subtitle:
        "We integrate strategy, processes, controllership, technology and intelligence into a single ecosystem — so your company grows with control, predictability and data-driven decisions.",
      primary_cta: "Book a Diagnostic",
      secondary_cta: "Talk on WhatsApp",
      metric_1: { value: "6", label: "Integrated pillars" },
      metric_2: { value: "10+", label: "Years of experience" },
      metric_3: { value: "100%", label: "Result-driven" },
    },
    problems: {
      overline: "Reality Check",
      title: "The pains holding your company back",
      subtitle:
        "Do any of these look familiar? Most businesses in transition face the same recurring patterns.",
      items: [
        "Revenue grows, but profit does not follow.",
        "Cash flow is disorganized and unpredictable.",
        "Lack of KPIs to make confident decisions.",
        "Informal, people-dependent processes.",
        "Excessive dependency on the owner day to day.",
        "Too many spreadsheets, weak controls.",
        "Little visibility over costs, margins and results.",
        "Excessive rework, low productivity.",
      ],
    },
    manifesto: {
      overline: "Sapientia Manifesto",
      title: "Management is not a set of tools. Management is a system.",
      lines: [
        "Every organization is born from an idea. Few, though, are built to grow sustainably.",
        "Over time, processes get created without standards, controls stop keeping up with the operation, and decisions rely more on people's experience than on trustworthy information.",
        "The problem is rarely a lack of effort — it is the absence of an architecture that connects strategy, people, processes, information and technology.",
        "We design, structure and implement management systems that turn organizations into predictable, efficient and data-driven environments.",
      ],
      quote: "Growth matters. Growth with control is essential.",
      quote_author: "— Sapientia Consultores Associados",
    },
    solution: {
      overline: "Our Approach",
      title: "We don't just deliver recommendations. We design the architecture that sustains growth.",
      body_1:
        "We believe strong companies do not depend exclusively on extraordinary leaders. They depend on consistent processes, trustworthy information, solid governance and technology strategically applied.",
      body_2:
        "That is why our work spans from a deep understanding of the business to continuous KPI monitoring — always integrating strategy, operations and technology into a single model.",
      bullets: [
        "Strategy translated into objectives, KPIs and initiatives (BSC).",
        "Processes, controllership and technology as one ecosystem.",
        "Dashboards feeding the Strategic Map in real time.",
      ],
    },
    architecture: {
      overline: "Our Methodology",
      title: "Management Architecture",
      lead:
        "A methodology connecting strategic planning, Balanced Scorecard, processes, controllership, information systems, Business Intelligence, dashboards and Artificial Intelligence into a single structure.",
      caption: "Strategy · Processes · Technology · Intelligence · Results",
      pillars_overline: "Six Integrated Pillars",
      pillars_title: "The invisible structure that makes a company grow with solidity",
      pillars: [
        { title: "Strategic Architecture", desc: "Mission, vision, values, Strategic Map (BSC), objectives, KPIs and governance." },
        { title: "Process Architecture", desc: "Mapping, standardization, redesign and process-based management aligned to strategy." },
        { title: "Information Architecture", desc: "Data structuring, systems integration and indicator quality." },
        { title: "Technology Architecture", desc: "Automation, custom software, AI, integrations and digital transformation." },
        { title: "Control Architecture", desc: "Controllership, costs, budgeting, risks, compliance and organizational performance." },
        { title: "Intelligence Architecture", desc: "Business Intelligence, executive dashboards, analytics and continuous monitoring." },
      ],
      cycle_title: "A permanent management cycle",
      cycle: [
        "Strategic Planning",
        "Balanced Scorecard",
        "Organizational Processes",
        "Information Systems",
        "Controllership",
        "Business Intelligence",
        "Managerial Dashboards",
        "KPI Monitoring",
        "Decision-Making",
        "Continuous Improvement",
      ],
    },
    architecture_cycle: {
      center: {
        overline: "Cycle",
        title_line_1: "Management",
        title_line_2: "Architecture",
      },
      phases: [
        { name: "Plan", items: ["Strategic Planning", "Balanced Scorecard", "Objectives & KPIs"] },
        { name: "Structure", items: ["Organizational Processes", "Information Systems", "Controllership"] },
        { name: "Monitor", items: ["Business Intelligence", "Managerial Dashboards", "KPI Monitoring"] },
        { name: "Evolve", items: ["Decision-Making", "Continuous Improvement"] },
      ],
    },
    metodo: {
      overline: "Sapientia Method®",
      title: "From strategy to execution. From information to intelligence.",
      subtitle:
        "Seven stages connected by centralized governance and controllership — sustained by the transversal pillars of people, culture, data, technology and ethics. This is the method that turns strategy into real results, and real results into competitive advantage.",
      quote:
        "This is the cycle that turns strategy into results and keeps your organization always one step ahead.",
      chips: ["Plan", "Measure", "Execute", "Analyze", "Evolve"],
    },
    services: {
      overline: "What we do",
      title: "Integrated management & technology services",
      subtitle:
        "A connected portfolio that solves the problem end-to-end — from strategy to automated execution.",
      items: [
        { title: "Strategic Controllership", desc: "Financial structuring, budgeting, managerial P&L and KPIs for data-driven decisions." },
        { title: "Strategic Planning", desc: "Vision, goals and action plans with clear indicators and execution cadence." },
        { title: "Process Management", desc: "Mapping, standardization and optimization of core processes to scale with order." },
        { title: "Business Intelligence", desc: "Dashboards and executive panels that turn data into real-time decisions." },
        { title: "Process Automation", desc: "Automating administrative, financial and operational routines across systems." },
        { title: "Artificial Intelligence", desc: "AI applied to management: forecasting, pattern analysis, internal agents and copilots." },
        { title: "Custom Software", desc: "Tailored solutions when off-the-shelf software cannot solve the real operational problem." },
        { title: "Public Sector Consulting", desc: "Modernization, transparency and efficiency for municipalities, agencies and consortiums." },
      ],
    },
    differentials: {
      overline: "Why Sapientia",
      title: "Differentiators that sustain a high-standard delivery",
      items: [
        { title: "10+ years of experience", desc: "Solid track record on complex projects across private and public sectors." },
        { title: "Multidisciplinary team", desc: "Management, controllership, data engineering and development in one team." },
        { title: "Management + technology", desc: "We do not separate consulting from tech — we run both as one." },
        { title: "Tailored solutions", desc: "No boilerplate. We model each project to the company's real context." },
        { title: "Result-focused", desc: "Objective indicators track the before and after of every implementation." },
        { title: "Practical implementation", desc: "We stay next to the client's team until the new model is running." },
        { title: "Continuous follow-up", desc: "A post go-live improvement cycle to sustain the gains." },
      ],
    },
    process: {
      overline: "How We Work",
      title: "A clear method in five stages",
      subtitle: "From deeply understanding the business to continuously monitoring results.",
      steps: [
        { title: "Diagnostic", desc: "Full immersion: finance, processes, people, systems and culture." },
        { title: "Planning", desc: "Action plan, prioritization and design of success indicators." },
        { title: "Implementation", desc: "Joint execution, process redesign and controllership structuring." },
        { title: "Automation", desc: "Digitization, systems integration, dashboards and applied AI." },
        { title: "Monitoring", desc: "Ongoing follow-up of KPIs and refinement of the operating model." },
      ],
    },
    technology: {
      overline: "Technology backing every decision",
      title: "Business intelligence, data and automation in a single ecosystem",
      subtitle:
        "We select and integrate the right tools for each reality — from SMB back-offices to public agency environments.",
      stack: [
        "Power BI",
        "SQL Server",
        "Python",
        "APIs & Integrations",
        "No-Code",
        "Artificial Intelligence",
        "Executive Dashboards",
        "Process Automation",
      ],
    },
    cta_final: {
      overline: "Business Diagnostic",
      title: "It's time to run your company with intelligence.",
      subtitle:
        "Request an Intelligent Business Diagnostic and uncover the biggest levers of result inside your operation.",
      button: "I Want a Business Diagnostic",
    },
    form: {
      overline: "Intelligent Business Diagnostic",
      title: "Start with understanding — not with a sales pitch.",
      subtitle:
        "Fill in the details below. They will serve as the base for our first meeting and a truly tailored proposal.",
      fields: {
        full_name: "Full name",
        email: "Corporate e-mail",
        whatsapp: "WhatsApp",
        company: "Company",
        role: "Role",
        sector: "Industry",
        size: "Company size",
        size_placeholder: "Select company size",
        size_options: [
          { value: "1-10", label: "Up to 10 employees" },
          { value: "11-50", label: "11 to 50 employees" },
          { value: "51-200", label: "51 to 200 employees" },
          { value: "201-500", label: "201 to 500 employees" },
          { value: "500+", label: "500+ employees" },
          { value: "public", label: "Public agency / Authority" },
        ],
        challenges: "Current key challenges",
        challenges_ph: "e.g.: lack of KPIs, disorganized cash flow, owner dependency…",
        goals: "Goals for the next 12 months",
        goals_ph: "e.g.: professionalize management, scale operations, deploy controllership…",
        consent: "I authorize Sapientia to contact me and I agree with the Privacy Policy.",
        submit: "Request Diagnostic",
        sending: "Sending…",
      },
      success_title: "We received your information.",
      success_body:
        "One of our consultants will reach out shortly to schedule the Intelligent Business Diagnostic.",
      error: "We couldn't submit your data. Please try again in a moment.",
      validation: {
        required: "Required field",
        email: "Invalid e-mail",
        consent: "Consent is required",
      },
    },
    footer: {
      tagline:
        "Combining management, data and technology to structure companies that grow with control.",
      nav_title: "Navigation",
      contact_title: "Contact",
      legal_title: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      copyright: "© {year} Sapientia Consultores Associados EIRELI. All rights reserved.",
      address: "Curitiba — Paraná, Brazil",
    },
    whatsapp_msg: "Hello! I came from the Sapientia website and would like to talk about management.",
  },

  es: {
    lang_label: "Español",
    nav: {
      about: "Manifiesto",
      architecture: "Arquitectura",
      services: "Servicios",
      process: "Cómo Trabajamos",
      technology: "Tecnología",
      diagnostic: "Diagnóstico",
      cta: "Agendar Diagnóstico",
    },
    hero: {
      overline: "Arquitectura de la Gestión · Sapientia",
      title: "Diseñamos la Arquitectura de la Gestión que sostiene empresas en crecimiento.",
      subtitle:
        "Integramos estrategia, procesos, controladuría, tecnología e inteligencia en un solo ecosistema — para que tu empresa crezca con control, previsibilidad y decisiones basadas en datos.",
      primary_cta: "Agendar Diagnóstico",
      secondary_cta: "Hablar por WhatsApp",
      metric_1: { value: "6", label: "Pilares integrados" },
      metric_2: { value: "10+", label: "Años de experiencia" },
      metric_3: { value: "100%", label: "Enfoque en resultados" },
    },
    problems: {
      overline: "Diagnóstico de Realidad",
      title: "Los dolores que frenan el crecimiento de tu empresa",
      subtitle:
        "¿Reconoces alguno de estos escenarios? La mayoría de los negocios en transición enfrentan los mismos patrones.",
      items: [
        "La facturación crece, pero el beneficio no acompaña.",
        "Flujo de caja desorganizado e impredecible.",
        "Falta de indicadores para decidir con seguridad.",
        "Procesos informales y dependientes de personas.",
        "Dependencia excesiva del dueño en el día a día.",
        "Demasiadas planillas, controles frágiles.",
        "Poca visibilidad de costos, márgenes y resultado.",
        "Mucho retrabajo, baja productividad.",
      ],
    },
    manifesto: {
      overline: "Manifiesto Sapientia",
      title: "La gestión no es un conjunto de herramientas. La gestión es un sistema.",
      lines: [
        "Toda organización nace de una idea. Pero pocas están construidas para crecer de forma sostenible.",
        "Con el tiempo, los procesos se crean sin estándar, los controles dejan de acompañar la operación y las decisiones dependen más de la experiencia de las personas que de información confiable.",
        "El problema rara vez está en la falta de esfuerzo — está en la ausencia de una arquitectura que conecte estrategia, personas, procesos, información y tecnología.",
        "Diseñamos, estructuramos e implementamos sistemas de gestión que transforman organizaciones en entornos previsibles, eficientes y orientados por datos.",
      ],
      quote: "Crecer es importante. Crecer con control es esencial.",
      quote_author: "— Sapientia Consultores Associados",
    },
    solution: {
      overline: "Nuestro Enfoque",
      title: "No entregamos solo recomendaciones. Diseñamos la arquitectura que sostiene el crecimiento.",
      body_1:
        "Creemos que las empresas fuertes no dependen exclusivamente de líderes extraordinarios. Dependen de procesos consistentes, información confiable, gobernanza sólida y tecnología aplicada de forma estratégica.",
      body_2:
        "Por eso nuestra actuación va desde la comprensión profunda del negocio hasta el monitoreo continuo de los indicadores — siempre integrando estrategia, operación y tecnología en un solo modelo.",
      bullets: [
        "Estrategia traducida en objetivos, KPIs e iniciativas (BSC).",
        "Procesos, controladuría y tecnología como un solo ecosistema.",
        "Dashboards que alimentan el Mapa Estratégico en tiempo real.",
      ],
    },
    architecture: {
      overline: "Nuestra Metodología",
      title: "Arquitectura de la Gestión",
      lead:
        "Una metodología que conecta planeación estratégica, Balanced Scorecard, procesos, controladuría, sistemas de información, Business Intelligence, dashboards e Inteligencia Artificial en una sola estructura.",
      caption: "Estrategia · Procesos · Tecnología · Inteligencia · Resultados",
      pillars_overline: "Seis Pilares Integrados",
      pillars_title: "La estructura invisible que hace crecer a una empresa con solidez",
      pillars: [
        { title: "Arquitectura Estratégica", desc: "Misión, visión, valores, Mapa Estratégico (BSC), objetivos, KPIs y gobernanza." },
        { title: "Arquitectura de Procesos", desc: "Mapeo, estandarización, rediseño y gestión por procesos alineados a la estrategia." },
        { title: "Arquitectura de la Información", desc: "Estructuración de datos, integración de sistemas y calidad de los indicadores." },
        { title: "Arquitectura Tecnológica", desc: "Automatización, desarrollo de sistemas, IA, integraciones y transformación digital." },
        { title: "Arquitectura de Control", desc: "Controladuría, costos, presupuesto, riesgos, cumplimiento y desempeño organizacional." },
        { title: "Arquitectura de la Inteligencia", desc: "Business Intelligence, dashboards ejecutivos, analytics y monitoreo continuo." },
      ],
      cycle_title: "Un ciclo permanente de gestión",
      cycle: [
        "Planeación Estratégica",
        "Balanced Scorecard",
        "Procesos Organizacionales",
        "Sistemas de Información",
        "Controladuría",
        "Business Intelligence",
        "Dashboards Gerenciales",
        "Monitoreo de KPIs",
        "Toma de Decisión",
        "Mejora Continua",
      ],
    },
    architecture_cycle: {
      center: {
        overline: "Ciclo",
        title_line_1: "Arquitectura",
        title_line_2: "de la Gestión",
      },
      phases: [
        { name: "Planear", items: ["Planeación Estratégica", "Balanced Scorecard", "Objetivos y KPIs"] },
        { name: "Estructurar", items: ["Procesos Organizacionales", "Sistemas de Información", "Controladuría"] },
        { name: "Monitorear", items: ["Business Intelligence", "Dashboards Gerenciales", "Monitoreo de KPIs"] },
        { name: "Evolucionar", items: ["Toma de Decisión", "Mejora Continua"] },
      ],
    },
    metodo: {
      overline: "Método Sapientia®",
      title: "De la estrategia a la ejecución. De la información a la inteligencia.",
      subtitle:
        "Siete etapas conectadas por una gobernanza y controladuría centralizadas — sostenidas por los pilares transversales de personas, cultura, datos, tecnología y ética. Es el método que transforma la estrategia en resultado real, y el resultado real en ventaja competitiva.",
      quote:
        "Este es el ciclo que transforma la estrategia en resultados y mantiene a tu organización siempre un paso adelante.",
      chips: ["Planear", "Medir", "Ejecutar", "Analizar", "Evolucionar"],
    },
    services: {
      overline: "Qué hacemos",
      title: "Servicios integrados de gestión y tecnología",
      subtitle:
        "Un portafolio conectado que resuelve el problema de punta a punta — de la estrategia a la ejecución automatizada.",
      items: [
        { title: "Controladuría Estratégica", desc: "Estructuración financiera, presupuesto, P&L gerencial e indicadores para decidir con datos." },
        { title: "Planeación Estratégica", desc: "Visión, metas y plan de acción con indicadores claros y cadencia de ejecución." },
        { title: "Gestión de Procesos", desc: "Mapeo, estandarización y optimización de procesos clave para escalar con orden." },
        { title: "Business Intelligence", desc: "Dashboards y paneles ejecutivos que transforman datos en decisiones en tiempo real." },
        { title: "Automatización de Procesos", desc: "Automatización de rutinas administrativas, financieras y operativas con integración de sistemas." },
        { title: "Inteligencia Artificial", desc: "IA aplicada a la gestión: pronósticos, análisis de patrones, agentes internos y copilotos." },
        { title: "Desarrollo de Sistemas", desc: "Soluciones a medida cuando el software estándar no resuelve el problema real." },
        { title: "Consultoría Pública", desc: "Modernización, transparencia y eficiencia para municipios, entidades y consorcios públicos." },
      ],
    },
    differentials: {
      overline: "Por qué Sapientia",
      title: "Diferenciales que sostienen una entrega de alto estándar",
      items: [
        { title: "Más de una década de experiencia", desc: "Trayectoria sólida en proyectos complejos, sector privado y público." },
        { title: "Equipo multidisciplinario", desc: "Gestión, controladuría, ingeniería de datos y desarrollo en un solo equipo." },
        { title: "Gestión + tecnología", desc: "No separamos consultoría de TI — trabajamos ambos como uno solo." },
        { title: "Soluciones personalizadas", desc: "Nada de recetas listas. Modelamos cada proyecto al contexto real." },
        { title: "Foco en resultados", desc: "Indicadores objetivos siguen el antes y después de cada implantación." },
        { title: "Implantación práctica", desc: "Nos quedamos junto al equipo del cliente hasta que el nuevo modelo esté en marcha." },
        { title: "Acompañamiento continuo", desc: "Ciclo de mejora continua post go-live para sostener las ganancias." },
      ],
    },
    process: {
      overline: "Cómo Trabajamos",
      title: "Un método claro en cinco etapas",
      subtitle: "Del entendimiento profundo del negocio al monitoreo continuo de los resultados.",
      steps: [
        { title: "Diagnóstico", desc: "Inmersión completa: finanzas, procesos, personas, sistemas y cultura." },
        { title: "Planeación", desc: "Plan de acción, priorización y diseño de indicadores de éxito." },
        { title: "Implantación", desc: "Ejecución conjunta, rediseño de procesos y estructuración de la controladuría." },
        { title: "Automatización", desc: "Digitalización, integración de sistemas, dashboards e IA aplicada." },
        { title: "Monitoreo", desc: "Seguimiento continuo de KPIs y refinamiento del modelo." },
      ],
    },
    technology: {
      overline: "Tecnología que respalda la decisión",
      title: "Inteligencia de negocios, datos y automatización en un solo ecosistema",
      subtitle:
        "Seleccionamos e integramos las herramientas correctas para cada realidad — de la pyme al ente público.",
      stack: [
        "Power BI",
        "SQL Server",
        "Python",
        "APIs e Integraciones",
        "No-Code",
        "Inteligencia Artificial",
        "Dashboards Ejecutivos",
        "Automatización de Procesos",
      ],
    },
    cta_final: {
      overline: "Diagnóstico Empresarial",
      title: "Es hora de administrar tu empresa con inteligencia.",
      subtitle:
        "Solicita un Diagnóstico Empresarial Inteligente y descubre las mayores palancas de resultado de tu negocio.",
      button: "Quiero un Diagnóstico Empresarial",
    },
    form: {
      overline: "Diagnóstico Empresarial Inteligente",
      title: "Comienza por entender — no por vender.",
      subtitle:
        "Completa la información abajo. Servirá como base para nuestra primera reunión y una propuesta realmente a medida.",
      fields: {
        full_name: "Nombre completo",
        email: "Correo corporativo",
        whatsapp: "WhatsApp",
        company: "Empresa",
        role: "Cargo",
        sector: "Sector de actividad",
        size: "Tamaño de la empresa",
        size_placeholder: "Selecciona el tamaño",
        size_options: [
          { value: "1-10", label: "Hasta 10 colaboradores" },
          { value: "11-50", label: "11 a 50 colaboradores" },
          { value: "51-200", label: "51 a 200 colaboradores" },
          { value: "201-500", label: "201 a 500 colaboradores" },
          { value: "500+", label: "Más de 500 colaboradores" },
          { value: "public", label: "Ente público / Autoridad" },
        ],
        challenges: "Principales desafíos actuales",
        challenges_ph: "Ej.: falta de indicadores, flujo de caja desorganizado, dependencia del socio…",
        goals: "Objetivos para los próximos 12 meses",
        goals_ph: "Ej.: profesionalizar la gestión, escalar la operación, implantar controladuría…",
        consent: "Autorizo a Sapientia a contactarme y acepto la Política de Privacidad.",
        submit: "Solicitar Diagnóstico",
        sending: "Enviando…",
      },
      success_title: "Recibimos tu información.",
      success_body:
        "En breve un consultor se pondrá en contacto para agendar el Diagnóstico Empresarial Inteligente.",
      error: "No pudimos enviar los datos. Intenta nuevamente en unos instantes.",
      validation: {
        required: "Campo obligatorio",
        email: "Correo inválido",
        consent: "Es necesario autorizar el contacto",
      },
    },
    footer: {
      tagline:
        "Combinando gestión, datos y tecnología para estructurar empresas que crecen con control.",
      nav_title: "Navegación",
      contact_title: "Contacto",
      legal_title: "Legal",
      privacy: "Política de Privacidad",
      terms: "Términos de Uso",
      copyright: "© {year} Sapientia Consultores Associados EIRELI. Todos los derechos reservados.",
      address: "Curitiba — Paraná, Brasil",
    },
    whatsapp_msg: "¡Hola! Vine desde el sitio de Sapientia y me gustaría hablar sobre gestión.",
  },
};

export const LANG_CODES = ["pt", "en", "es"];
